import React, { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTheme } from '../contexts/ThemeContext';
import '../@style/tradingview.css';

const TradingView = ({ selectedSymbol }) => {
  const { isDarkMode } = useTheme();
  const chartContainerRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);
  const [converterAmount, setConverterAmount] = useState(1);
  const [converterFrom, setConverterFrom] = useState('');
  const [converterTo, setConverterTo] = useState('');
  const [exchangeRates, setExchangeRates] = useState({});
  const [goldOptions, setGoldOptions] = useState([]);
  const [activeTimeframe, setActiveTimeframe] = useState('D');
  const [isMobile, setIsMobile] = useState(false);
  
  // Varsayılan olarak GAUTRY seçili olsun
  const defaultSymbol = selectedSymbol || 'GAUTRY';

  // Mobil cihaz kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // WebSocket bağlantısı
  const { data } = useWebSocket('wss://sslsocket.aifanet.com/5.250.255.84/13698');
  
  // ALTIN fiyatlarını güncelle (ذهب + الصرافية kategorileri)
  useEffect(() => {
    if (data && Array.isArray(data)) {
      const rates = {};
      const options = [];
      
      data.forEach(item => {
        if ((item.Category === 'ذهب' || item.Category === 'الصرافية') && item.Code && item.Ask) {
          const code = item.Code;
          const ask = parseFloat(item.Ask) || 0;
          if (ask > 0) {
            rates[code] = ask;
            options.push({ code, name: item.Description || code });
          }
        }
      });
      
      if (Object.keys(rates).length > 0) {
        rates['TRY'] = 1; // TRY temel para birimi
        const allOptions = [{ code: 'TRY', name: 'Türk Lirası' }, ...options];
        setExchangeRates(rates);
        setGoldOptions(allOptions);
        const hasAltinCode = options.find(o => 
          (o.code && o.code.toUpperCase().includes('HAS')) || 
          (o.name && o.name.toUpperCase().includes('HAS'))
        )?.code || options[0]?.code;
        setConverterFrom(prev => (prev && rates[prev] ? prev : hasAltinCode || 'TRY'));
        setConverterTo(prev => (prev && rates[prev] ? prev : 'TRY'));
      }
    }
  }, [data]);


  // TradingView grafiğini yükle
  useEffect(() => {
    if (defaultSymbol && chartContainerRef.current) {
      // Mevcut grafiği temizle - widget'ı temizleme, sadece container'ı temizle
      if (chartContainerRef.current) {
        chartContainerRef.current.innerHTML = '';
      }

      // TradingView widget'ını yükle
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        if (window.TradingView && chartContainerRef.current) {
          try {
            // Container yüksekliğini al
            const containerHeight = chartContainerRef.current.clientHeight || (isMobile ? 500 : 600);
            
            const chart = new window.TradingView.widget({
              symbol: defaultSymbol,
              interval: activeTimeframe,
              timezone: 'Europe/Istanbul',
              theme: isDarkMode ? 'dark' : 'light',
              style: '1',
              locale: 'tr',
              toolbar_bg: '#f1f3f6',
              enable_publishing: false,
              allow_symbol_change: true,
              container_id: chartContainerRef.current.id,
              width: '100%',
              height: containerHeight || '100%',
              autosize: true,
              studies: [
                'Volume@tv-basicstudies'
              ],
              show_popup_button: true,
              popup_width: isMobile ? '100%' : '1000',
              popup_height: isMobile ? '100%' : '650',
              // Mobil için ek optimizasyonlar
              hide_side_toolbar: isMobile,
              hide_legend: isMobile,
              save_image: false,
              // Touch cihazlar için
              enable_touch: true,
              // Mobil performans
              library_path: '/charting_library/',
              custom_css_url: '',
            });
            setChartInstance(chart);
          } catch (error) {
            console.error('TradingView widget oluşturma hatası:', error);
          }
        }
      };
      document.head.appendChild(script);

      return () => {
        // Widget'ı temizleme, sadece container'ı temizle
        if (chartContainerRef.current) {
          chartContainerRef.current.innerHTML = '';
        }
      };
    }
  }, [defaultSymbol, isDarkMode, activeTimeframe, isMobile]);

  // Timeframe değiştirme
  const handleTimeframeChange = (timeframe) => {
    setActiveTimeframe(timeframe);
    
    // Mobil cihazlarda timeframe değişikliğinde smooth scroll
    if (isMobile && chartContainerRef.current) {
      chartContainerRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Çevirici hesaplama (altın + TRY arası)
  const convertCurrency = () => {
    if (Object.keys(exchangeRates).length === 0 || !converterFrom || !converterTo) {
      return '0.0000';
    }
    if (converterAmount <= 0) return '0.0000';
    
    const fromRate = exchangeRates[converterFrom];
    const toRate = exchangeRates[converterTo];
    
    if (fromRate === undefined || fromRate === null) return '0.0000';
    if (toRate === undefined || toRate === null || (converterTo !== 'TRY' && toRate === 0)) return '0.0000';
    
    // Aynı birim: 1:1
    if (converterFrom === converterTo) return converterAmount.toFixed(4);
    
    // TRY'den altına: miktar / altınFiyatı (örn: 2500 TRY = 2500/2500 = 1 gram)
    if (converterFrom === 'TRY') {
      return (converterAmount / toRate).toFixed(4);
    }
    // Altından TRY'ye: miktar * altınFiyatı (örn: 1 gram = 2500 TRY)
    if (converterTo === 'TRY') {
      return (converterAmount * fromRate).toFixed(4);
    }
    // Altın birimleri arası: miktar * (fromFiyat / toFiyat)
    const result = (converterAmount * fromRate) / toRate;
    return result.toFixed(4);
  };

  // Sonuç formatı: 7420.2263 -> 7.420,23 (Türkçe sayı formatı)
  const formatResult = (valueStr) => {
    const num = parseFloat(valueStr);
    if (isNaN(num)) return '0';
    return num.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Mobil için kısa timeframe isimleri
  const getTimeframeLabel = (timeframe) => {
    if (isMobile) {
      const labels = {
        'D': 'G',
        '60': '1sa',
        '30': '30dk',
        '1': '1dk'
      };
      return labels[timeframe] || timeframe;
    }
    return timeframe;
  };

  return (
    <div className={`tradingview-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="chart-section">
        <div className="chart-header">
          <h3>{defaultSymbol}</h3>
          <div className="chart-controls">
            {['D', '60', '30', '1'].map(timeframe => (
              <button 
                key={timeframe}
                className={`timeframe-btn ${activeTimeframe === timeframe ? 'active' : ''}`}
                onClick={() => handleTimeframeChange(timeframe)}
                aria-label={`${timeframe} zaman dilimi`}
              >
                {getTimeframeLabel(timeframe)}
              </button>
            ))}
          </div>
        </div>
        
        <div 
          id="tradingview-chart" 
          ref={chartContainerRef}
          className="chart-container"
        >
          {!defaultSymbol && (
            <div className="no-chart-selected">
              <p>Grafik görüntülemek için bir parite seçin</p>
            </div>
          )}
        </div>
      </div>

      <div className="converter-section">
        <h3>Çevirici</h3>
        <div className="converter-form">
          <div className="input-group">
            <label htmlFor="amount-input">Miktar:</label>
            <input
              id="amount-input"
              type="number"
              value={converterAmount}
              onChange={(e) => setConverterAmount(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              inputMode="decimal"
              autoComplete="off"
            />
          </div>
          
          <div className="input-group">
            <label htmlFor="from-select">Nereden:</label>
            <select 
              id="from-select"
              value={converterFrom} 
              onChange={(e) => setConverterFrom(e.target.value)}
            >
              {goldOptions.map(opt => (
                <option key={opt.code} value={opt.code}>
                  {opt.code} - {opt.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="input-group">
            <label htmlFor="to-select">Nereye:</label>
            <select 
              id="to-select"
              value={converterTo} 
              onChange={(e) => setConverterTo(e.target.value)}
            >
              {goldOptions.map(opt => (
                <option key={opt.code} value={opt.code}>
                  {opt.code} - {opt.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="conversion-result">
            <label>Sonuç:</label>
            <div className="result-value">
              {formatResult(convertCurrency())} {converterTo}
            </div>
          </div>
        </div>
        
        <div className="current-rates">
          <h4>Güncel Fiyatlar</h4>
          <div className="rates-grid">
            {goldOptions
              .filter(opt => opt.code !== 'TRY' && exchangeRates[opt.code] !== undefined)
              .slice(0, isMobile ? 4 : 6)
              .map(opt => (
                <div key={opt.code} className="rate-item">
                  <span className="rate-code">{opt.code}</span>
                  <span className="rate-value">
                    {exchangeRates[opt.code]?.toLocaleString('tr-TR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingView;
